import React from 'react';
import { observer } from 'mobx-react';
import {
  Theme, Caption,
} from '@routific/hive';
import styled from 'styled-components';
import AssignedTo from '../AssignedTo';
import { RouteDtoModelType } from '../../../store/mst/models';
import RouteNameWithStatus from './RouteNameWithStatus/index';
import RouteHeaderRow from './RouteHeaderRow';
import RouteTitleWithPendingChanges from '../RouteTitleWithPendingChanges';

interface Props {
  route: RouteDtoModelType;
  isHovered: boolean;
  withTitle: boolean;
  pending: boolean;
  withAssignDriver?: boolean;
  withStats?: boolean;
  showCheckbox?: boolean;
  isChecked?: boolean;
  onCheckboxChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  routeUuid?: string;
}

const InlineContainer = styled.div<{ $withStats?: boolean }>`
  display: ${($withStats) => (!$withStats ? 'inline-flex' : 'flex')};
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  margin-bottom: ${Theme.Spacing.xsSpacing};
`;

const CardHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${Theme.Spacing.smSpacing};
`;

const DriverRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

const RouteCardHeader: React.FunctionComponent<Props> = ({
  route,
  withTitle,
  pending,
  withAssignDriver,
  withStats,
  showCheckbox,
  isChecked,
  onCheckboxChange,
  routeUuid,
}) => {
  const displayTime: boolean = !withStats && (!!route.shiftStartTime || !!route.shiftEndTime);
  const showNameRow = withTitle || showCheckbox;
  const hasUnpublishedChanges = route.hasPublishablePendingChanges;

  const nameRow = showNameRow && (
    <RouteNameWithStatus
      name={withTitle ? route.name : null}
      showCheckbox={showCheckbox}
      isChecked={isChecked}
      onCheckboxChange={onCheckboxChange}
      variant={withStats ? 'title' : 'body'}
      routeUuid={routeUuid}
    />
  );

  const titleWithChip = withTitle && nameRow && (
    <RouteTitleWithPendingChanges
      title={nameRow}
      showPendingChanges={hasUnpublishedChanges}
    />
  );

  const assignedTo = (
    <AssignedTo
      size="small"
      route={route}
      online={route.isOnline}
      pending={pending}
      withLabel={!!withStats}
      withAssignDriver={withAssignDriver}
    />
  );

  return (
    <CardHeaderContainer>
      {displayTime && (
        <InlineContainer $withStats={withStats}>
          <Caption type="secondary">
            {`${route.shiftStartTimeString} – ${route.shiftEndTimeString}`}
          </Caption>
        </InlineContainer>
      )}
      {withStats ? (
        <>
          {titleWithChip}
          <DriverRow>{assignedTo}</DriverRow>
        </>
      ) : (
        <RouteHeaderRow withTitle={withTitle}>
          {titleWithChip || nameRow}
          {assignedTo}
        </RouteHeaderRow>
      )}
    </CardHeaderContainer>
  );
};

export default observer(RouteCardHeader);
